<template>
    <div>
        <b-container fluid>
            <b-row>
                <b-col>
                    <b-card>
                        <b-row>
                            <b-col sm="12" md="5">
                                <periods
                                    :state="null"
                                    class="flex-grow-1"
                                    :label="$t('form.label.periods')"
                                    :value.sync="selectedPeriods"
                                    applyScopeNotArchived
                                />
                            </b-col>
                            <b-col sm="12" md="5">
                                <tags :state="null" class="flex-grow-1" :value.sync="selectedTags" />
                            </b-col>
                            <b-col sm="12" md="2">
                                <div class="h-100 d-flex justify-content-end justify-content-md-center mt-1">
                                    <b-button class="align-self-center" variant="primary" @click="search">
                                        {{ $t('dataTable.filter') }}
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>
        <b-container fluid>
            <b-alert show v-if="firstLoad" variant="light" class="d-flex justify-content-center">
                <b>{{ $t('reports.tags.helpMessage') }}</b>
            </b-alert>
            <loader :loader="isLoading.consolidated" v-else-if="isLoading.consolidated" />
            <template v-else>
                <b-row>
                    <b-col cols="12" md="4">
                        <b-card class="p-3" no-body>
                            <div>
                                <progress-info :progress="total.progress" :name="$t('reports.tags.average')" />
                                <progress-info
                                    :progress="objectives.progress"
                                    :quantity="objectives.quantity"
                                    :name="$t('objectives.objectives')"
                                    v-if="modules.has_okr"
                                />
                                <progress-info
                                    :progress="keyResults.progress"
                                    :quantity="keyResults.quantity"
                                    :name="$t('insights.keyResults')"
                                    v-if="modules.has_okr"
                                />
                                <progress-info
                                    :progress="goals.progress"
                                    :quantity="goals.quantity"
                                    :name="$t('targets.targets')"
                                    v-if="modules.has_goal"
                                />
                            </div>
                            <div class="mb-3">
                                <span>
                                    {{ $t('form.label.departments') }}
                                </span>
                                <template v-if="sections.length">
                                    <b-badge class="ml-2">
                                        {{ sections.length }}
                                    </b-badge>
                                    <div class="mt-1 d-flex flex-wrap">
                                        <b-badge
                                            v-for="section in sections.slice(0, sectionsToShow)"
                                            :key="section.id"
                                            class="ml-1 mb-1"
                                            variant="dark"
                                        >
                                            {{ section.name }}
                                        </b-badge>
                                        <template v-if="sections.length > sectionsToShow">
                                            <h4 class="ml-2 mb-0" id="sections-list">...</h4>
                                            <b-popover target="sections-list" placement="right" triggers="hover focus">
                                                <b-badge
                                                    v-for="section in sections.slice(sectionsToShow)"
                                                    :key="section.id"
                                                    class="ml-1"
                                                    variant="dark"
                                                >
                                                    {{ section.name }}
                                                </b-badge>
                                            </b-popover>
                                        </template>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="mt-1 d-flex flex-wrap">
                                        <div class="alert alert-info mb-0">
                                            {{ $t('reports.sections.empty') }}
                                        </div>
                                    </div>
                                </template>
                            </div>
                            <div class="mb-3">
                                <div class="mb-2">
                                    <span> {{ $t('reports.tags.involved') }}</span>
                                    <template v-if="users.length">
                                        <b-badge class="ml-2">
                                            {{ users.length }}
                                        </b-badge>
                                        <div class="mt-1 d-flex flex-wrap">
                                            <div v-for="user in users.slice(0, usersToShow)" :key="user.id">
                                                <avatar
                                                    class="mr-1"
                                                    :size="30"
                                                    :src="user.picture"
                                                    :username="user.name"
                                                />
                                            </div>
                                            <template v-if="users.length > usersToShow">
                                                <h4 id="users-list" class="">...</h4>
                                                <b-popover target="users-list" placement="right" triggers="hover">
                                                    <div class="d-flex flex-wrap">
                                                        <avatar
                                                            v-for="user in users.slice(usersToShow)"
                                                            class="mr-1"
                                                            :key="user.id"
                                                            :size="30"
                                                            :src="user.picture"
                                                            :username="user.name"
                                                        />
                                                    </div>
                                                </b-popover>
                                            </template>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </b-card>
                    </b-col>
                    <b-col>
                        <b-card>
                            <b-tabs>
                                <b-tab :title="$t('objectives.objectives')" active v-if="modules.has_okr">
                                    <loader :loader="isLoading.objectives" v-if="isLoading.objectives" />
                                    <entity-list
                                        v-else
                                        :entityType="'objectives'"
                                        :entities="objectives.list"
                                        :progress="objectives.progress"
                                        @changePage="changePage"
                                    />
                                </b-tab>
                                <b-tab :title="$t('insights.keyResults')" v-if="modules.has_okr">
                                    <loader :loader="isLoading.keyResults" v-if="isLoading.keyResults" />
                                    <entity-list
                                        v-else
                                        :entityType="'keyResults'"
                                        :entities="keyResults.list"
                                        :progress="keyResults.progress"
                                        @changePage="changePage"
                                    />
                                </b-tab>
                                <b-tab :title="$t('targets.targets')" v-if="modules.has_goal">
                                    <loader :loader="isLoading.goals" v-if="isLoading.goals" />
                                    <entity-list
                                        v-else
                                        :entityType="'goals'"
                                        :entities="goals.list"
                                        :progress="goals.progress"
                                        @changePage="changePage"
                                    />
                                </b-tab>
                            </b-tabs>
                        </b-card>
                    </b-col>
                </b-row>
            </template>
        </b-container>
    </div>
</template>

<script>
import { Periods, Tags } from '@/views/components/fields'
import api from '@/services/api'
import ProgressInfo from './components/ProgressInfo'
import EntityList from './components/EntityList'
import Avatar from 'vue-avatar'
import Loader from '@/views/components/loader'
import { getUserPermissions } from '@/services/user-permissions'

export default {
    name: 'TagReport',
    components: {
        Periods,
        Tags,
        ProgressInfo,
        EntityList,
        Avatar,
        Loader,
    },
    data() {
        return {
            selectedPeriods: [],
            selectedTags: [],
            isLoading: {
                consolidated: false,
                objectives: false,
                goals: false,
                keyResults: false,
            },
            objectives: {
                quantity: null,
                progress: null,
                list: {},
            },
            keyResults: {
                quantity: null,
                progress: null,
                list: {},
            },
            goals: {
                quantity: null,
                progress: null,
                list: {},
            },
            total: {
                quantity: null,
                progress: null,
            },
            sections: [],
            users: [],
            perPage: 5,
            usersToShow: 5,
            sectionsToShow: 5,
            firstLoad: true,
            isAdmin: false,
            modules: {},
        }
    },
    mounted() {
        const { modules, is_admin, is_values_master, is_target_master, is_god } = getUserPermissions()
        if (!is_admin && !is_values_master && !is_target_master && !is_god) {
            this.$router.push({
                path: '/v2/pages/error/403',
            })
        }
        this.modules = modules
    },
    methods: {
        search() {
            const requestBody = this.requestBody()
            this.getReport(requestBody)
            this.getKeyResults(requestBody, 1)
            this.getObjectives(requestBody, 1)
            this.getGoals(requestBody, 1)
        },
        requestBody() {
            const tags = this.selectedTags.map((p) => p.value)
            const periods = this.selectedPeriods.map((p) => p.value)

            if (!tags.length || !periods.length) {
                return null
            }
            return {
                tags,
                periods,
                perPage: this.perPage,
            }
        },
        changePage(params) {
            switch (params.entityType) {
                case 'keyResults':
                    this.getKeyResults(this.requestBody(), params.page)
                    break
                case 'objectives':
                    this.getObjectives(this.requestBody(), params.page)
                    break
                case 'goals':
                    this.getGoals(this.requestBody(), params.page)
                    break
            }
        },
        getReport(requestBody) {
            if (requestBody == null) return
            this.firstLoad = false
            this.isLoading.consolidated = true
            api.post('/tags/report', requestBody)
                .then((response) => {
                    const {
                        consolidated: { objectives, key_results, goals, total },
                        sections,
                        users,
                    } = response.data.data
                    Object.assign(this.objectives, objectives)
                    Object.assign(this.keyResults, key_results)
                    Object.assign(this.goals, goals)
                    this.total = total
                    this.sections = sections
                    this.users = users
                    this.isLoading.consolidated = false
                })
                .catch(console.error)
        },
        getKeyResults(requestBody, page = 1) {
            if (requestBody == null) return
            if (!this.modules.has_okr) return
            this.isLoading.keyResults = true
            api.post(`/tags/report/key-results?page=${page}`, requestBody)
                .then((response) => {
                    this.keyResults.list = response.data.data
                    this.isLoading.keyResults = false
                })
                .catch(console.error)
        },
        getObjectives(requestBody, page = 1) {
            if (requestBody == null) return
            if (!this.modules.has_okr) return
            this.isLoading.objectives = true
            api.post(`/tags/report/objectives?page=${page}`, requestBody)
                .then((response) => {
                    this.objectives.list = response.data.data
                    this.isLoading.objectives = false
                })
                .catch(console.error)
        },
        getGoals(requestBody, page = 1) {
            if (requestBody == null) return
            if (!this.modules.has_goal) return
            this.isLoading.goals = true
            api.post(`/tags/report/goals?page=${page}`, requestBody)
                .then((response) => {
                    this.goals.list = response.data.data
                    this.isLoading.goals = false
                })
                .catch(console.error)
        },
    },
}
</script>
